import React, { useState, useMemo } from "react";
import { TASKS, QUADRANT_META } from "./data.js";

const YES_NO = [
  { id: true, label: "Yes" },
  { id: false, label: "No" },
];

export default function ReconStage({ onComplete }) {
  const [index, setIndex] = useState(0);
  const [tags, setTags] = useState({});
  const [correctCount, setCorrectCount] = useState(0);
  const [flash, setFlash] = useState(null);

  const current = TASKS[index];
  const done = index >= TASKS.length;

  const quadrantCounts = useMemo(() => {
    const counts = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
    TASKS.forEach((t) => (counts[t.quadrant] += 1));
    return counts;
  }, []);

  const chooseTag = (field, value) => {
    if (!current) return;
    const existing = tags[current.id] || {};
    const updated = { ...existing, [field]: value };
    setTags((prev) => ({ ...prev, [current.id]: updated }));

    if (typeof updated.urgent === "boolean" && typeof updated.important === "boolean") {
      const bothCorrect = updated.urgent === current.correctUrgent && updated.important === current.correctImportant;
      if (bothCorrect) setCorrectCount((c) => c + 1);
      setFlash(bothCorrect ? "correct" : "off");
      setTimeout(() => {
        setFlash(null);
        setIndex((i) => i + 1);
      }, 550);
    }
  };

  if (done) {
    return (
      <div className="animate-popIn text-center">
        <p className="label-pixel mb-2 text-lime">Recon Complete</p>
        <h2 className="pixel-heading mb-4 text-base text-mist">Intel Logged</h2>

        <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
          <div className="flex justify-between">
            <span className="text-mist/60">Classified Correctly</span>
            <span className="text-lime">
              {correctCount}/{TASKS.length}
            </span>
          </div>
          {Object.entries(quadrantCounts).map(([q, count]) => (
            <div key={q} className="flex justify-between">
              <span className="text-mist/60">{QUADRANT_META[q].label}</span>
              <span className="text-mist">{count} item{count === 1 ? "" : "s"}</span>
            </div>
          ))}
        </div>

        <p className="mx-auto mb-6 max-w-sm text-xs text-mist/60">
          Notice how much of today's incoming pile is <span className="text-gold">urgent but not
          important</span> — that's the tyranny of the urgent. It'll never stop shouting. Q2 work
          builds your future, but only if you protect time for it on purpose.
        </p>

        <button onClick={() => onComplete({ correctCount })} className="btn-magenta">
          BUILD TODAY'S FORMATION ▶
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>RECON</span>
        <span>
          {index}/{TASKS.length} REVIEWED
        </span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Everything hitting your inbox and calendar today. Tag each by whether it's truly urgent,
        and whether it's actually important — those are two different questions.
      </p>

      <div
        key={current.id}
        className={`mb-5 border-2 border-black bg-ink p-5 transition-colors ${
          flash === "correct" ? "border-lime shadow-glow-cyan" : flash === "off" ? "border-danger" : ""
        }`}
      >
        <div className="mb-1 flex items-center justify-between text-xs text-mist/50">
          <span>{current.time}</span>
          <span className="text-gold">~{current.minutes} min</span>
        </div>
        <p className="pixel-heading text-sm leading-relaxed text-mist">{current.description}</p>
      </div>

      <div className="mb-4">
        <p className="label-pixel mb-2 text-mist/60">Is it Urgent?</p>
        <div className="grid grid-cols-2 gap-2">
          {YES_NO.map((opt) => {
            const selected = tags[current.id]?.urgent === opt.id;
            return (
              <button
                key={String(opt.id)}
                onClick={() => chooseTag("urgent", opt.id)}
                className={`border-2 px-3 py-2 font-display text-[9px] uppercase transition-colors ${
                  selected ? "border-cyan bg-cyan text-ink" : "border-mist/30 text-mist/70 hover:border-cyan"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="label-pixel mb-2 text-mist/60">Is it Important?</p>
        <div className="grid grid-cols-2 gap-2">
          {YES_NO.map((opt) => {
            const selected = tags[current.id]?.important === opt.id;
            return (
              <button
                key={String(opt.id)}
                onClick={() => chooseTag("important", opt.id)}
                className={`border-2 px-3 py-2 font-display text-[9px] uppercase transition-colors ${
                  selected ? "border-magenta bg-magenta text-ink" : "border-mist/30 text-mist/70 hover:border-magenta"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
